import { useState, useEffect, useCallback } from "react";
import { fetchEgdHubView } from "src/api/payload/egd";
import { isPayloadEgdSource } from "src/utils/egdSource";

/**
 * @typedef {import('src/api/payload/egd').EgdHubViewSlug} EgdHubViewSlug
 * @typedef {import('src/api/payload/egd').EgdHubCard} EgdHubCard
 */

/**
 * Loads a single EGD hub view from Payload CMS.
 *
 * @param {EgdHubViewSlug} view
 * @param {'en' | 'fr'} locale
 * @param {{ enabled?: boolean }} [options]
 * @returns {{
 *   cards: EgdHubCard[],
 *   progressiveJackpotThreshold: number | null,
 *   loading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>,
 *   isPayloadSource: boolean,
 * }}
 */
export function useEgdHubView(view, locale, options = {}) {
  const { enabled = true } = options;
  const isPayloadSource = isPayloadEgdSource();
  const shouldFetch = enabled && isPayloadSource;

  const [cards, setCards] = useState([]);
  const [progressiveJackpotThreshold, setProgressiveJackpotThreshold] = useState(null);
  const [loading, setLoading] = useState(shouldFetch);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!shouldFetch) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchEgdHubView(view, locale);
      setCards(response.cards);
      setProgressiveJackpotThreshold(response.progressiveJackpotThreshold);
    } catch (err) {
      console.error("Error fetching EGD hub view from Payload:", err);
      setError(err?.message || "Failed to load EGD hub view");
      setCards([]);
      setProgressiveJackpotThreshold(null);
    } finally {
      setLoading(false);
    }
  }, [view, locale, shouldFetch]);

  useEffect(() => {
    if (!shouldFetch) {
      setLoading(false);
      return;
    }
    refetch();
  }, [shouldFetch, refetch]);

  return {
    cards,
    progressiveJackpotThreshold,
    loading,
    error,
    refetch,
    isPayloadSource,
  };
}
